import { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Save, X, Clock, PauseCircle, ShieldOff, ShieldCheck, Play } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import api from '@/utils/api';
import { toast } from 'sonner';
import { DAYS } from '@/utils/helpers';

const empty = { name: '', address: '', city: '', phone: '', whatsapp_number: '', email_for_orders: '', estimated_delivery_time_minutes: 35, lat: '', lng: '', active: true };

export default function BranchesPage() {
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [hoursEdit, setHoursEdit] = useState(null);
  const [hours, setHours] = useState([]);
  const [newHour, setNewHour] = useState({ day_of_week: 0, start_time: '11:00', end_time: '22:00' });
  const [opsPanel, setOpsPanel] = useState(null);
  const [closeReason, setCloseReason] = useState('');
  const [loading, setLoading] = useState(true);

  const load = () => { setLoading(true); api.get('/sucursales').then(r => setBranches(r.data.sucursales)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const save = async () => {
    try {
      if (editing === 'new') { await api.post('/sucursales', form); toast.success('Sucursal creada'); }
      else { await api.put(`/sucursales/${editing.id}`, form); toast.success('Sucursal actualizada'); }
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Eliminar sucursal?')) return;
    try { await api.delete(`/sucursales/${id}`); toast.success('Eliminada'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  // Opening hours
  const loadHours = async (branchId) => {
    try {
      const { data } = await api.get(`/sucursales/${branchId}/opening-hours`);
      setHours(data.horarios);
      setHoursEdit(branchId);
    } catch { toast.error('Error cargando horarios'); }
  };

  const addHour = async () => {
    try {
      await api.post(`/sucursales/${hoursEdit}/opening-hours`, newHour);
      toast.success('Horario agregado');
      loadHours(hoursEdit);
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const removeHour = async (hourId) => {
    try { await api.delete(`/sucursales/${hoursEdit}/opening-hours/${hourId}`); loadHours(hoursEdit); }
    catch { toast.error('Error'); }
  };

  // Ops: close/pause
  const toggleClose = async (branchId, close) => {
    try {
      await api.post(`/sucursales/${branchId}/close-today`, { is_closed_today: close, closed_reason: close ? closeReason : null });
      toast.success(close ? 'Sucursal cerrada hoy' : 'Sucursal reabierta');
      setCloseReason(''); load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const pause = async (branchId, minutes) => {
    try {
      await api.post(`/sucursales/${branchId}/pause`, { minutes });
      toast.success(minutes > 0 ? `Pausada por ${minutes} min` : 'Pausa cancelada');
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Error'); }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  return (
    <div data-testid="admin-branches-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold">Sucursales</h2>
          <p className="text-sm text-zinc-500">{branches.length} sucursales</p>
        </div>
        <button onClick={() => { setEditing('new'); setForm(empty); }} className="flex items-center gap-2 bg-[#E84C1C] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#d43f12] transition-colors" data-testid="branch-add-btn">
          <Plus size={16} /> Nueva
        </button>
      </div>

      {/* Edit form */}
      {editing && (
        <div className="bg-zinc-900 border border-zinc-800/50 rounded-xl p-4 mb-6 space-y-3" data-testid="branch-form">
          <h3 className="text-sm font-semibold">{editing === 'new' ? 'Nueva sucursal' : `Editar: ${editing.name}`}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs text-zinc-500">Nombre</Label><Input value={form.name} onChange={e => set('name', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" data-testid="branch-name" /></div>
            <div><Label className="text-xs text-zinc-500">Direccion</Label><Input value={form.address} onChange={e => set('address', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" /></div>
            <div><Label className="text-xs text-zinc-500">Ciudad</Label><Input value={form.city} onChange={e => set('city', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" placeholder="Ej: Asunción" /></div>
            <div><Label className="text-xs text-zinc-500">Telefono</Label><Input value={form.phone} onChange={e => set('phone', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" /></div>
            <div><Label className="text-xs text-zinc-500">WhatsApp</Label><Input value={form.whatsapp_number} onChange={e => set('whatsapp_number', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" placeholder="595981123456" data-testid="branch-whatsapp" /></div>
            <div><Label className="text-xs text-zinc-500">Email para pedidos</Label><Input value={form.email_for_orders} onChange={e => set('email_for_orders', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" /></div>
            <div><Label className="text-xs text-zinc-500">Tiempo est. entrega (min)</Label><Input type="number" value={form.estimated_delivery_time_minutes} onChange={e => set('estimated_delivery_time_minutes', parseInt(e.target.value) || 0)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs text-zinc-500">Latitud</Label><Input type="number" step="any" value={form.lat} onChange={e => set('lat', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" placeholder="-25.28" /></div>
              <div><Label className="text-xs text-zinc-500">Longitud</Label><Input type="number" step="any" value={form.lng} onChange={e => set('lng', e.target.value)} className="bg-zinc-950 border-zinc-800 h-9 mt-1" placeholder="-57.57" /></div>
            </div>
            <div className="flex items-end gap-2 pb-1">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} className="accent-[#E84C1C]" /> Activa</label>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={save} className="flex items-center gap-1 bg-[#E84C1C] text-white text-sm px-4 py-2 rounded-lg hover:bg-[#d43f12]" data-testid="branch-save"><Save size={14} /> Guardar</button>
            <button onClick={() => setEditing(null)} className="flex items-center gap-1 text-sm text-zinc-400 px-3 py-2 rounded-lg hover:bg-zinc-800"><X size={14} /> Cancelar</button>
          </div>
        </div>
      )}

      {/* Branch cards */}
      <div className="space-y-3">
        {loading ? <div className="text-center text-zinc-500 py-8">Cargando...</div> :
          branches.map(b => {
            const isClosed = b.is_closed_today;
            const isPaused = b.pause_until && new Date(b.pause_until) > new Date();
            return (
              <div key={b.id} data-testid={`branch-row-${b.id}`} className="bg-zinc-900/50 border border-zinc-800/50 rounded-xl overflow-hidden">
                <div className="flex items-center gap-3 p-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{b.name}</p>
                      {b.active ? <span className="text-emerald-400 text-[10px] bg-emerald-400/10 px-1.5 py-0.5 rounded-full">Activa</span> : <span className="text-red-400 text-[10px] bg-red-400/10 px-1.5 py-0.5 rounded-full">Inactiva</span>}
                      {isClosed && <span className="text-red-400 text-[10px] bg-red-400/10 px-1.5 py-0.5 rounded-full">Cerrada hoy</span>}
                      {isPaused && <span className="text-amber-400 text-[10px] bg-amber-400/10 px-1.5 py-0.5 rounded-full">Pausada</span>}
                    </div>
                    <p className="text-xs text-zinc-500 mt-0.5 truncate">{b.address}</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => loadHours(b.id)} className="p-2 text-zinc-400 hover:text-blue-400 rounded-lg hover:bg-zinc-800 transition-colors" title="Horarios"><Clock size={15} /></button>
                    <button onClick={() => setOpsPanel(opsPanel === b.id ? null : b.id)} className="p-2 text-zinc-400 hover:text-amber-400 rounded-lg hover:bg-zinc-800 transition-colors" title="Operación"><PauseCircle size={15} /></button>
                    <button onClick={() => { setEditing(b); setForm({ name: b.name, address: b.address, city: b.city || '', phone: b.phone, whatsapp_number: b.whatsapp_number || '', email_for_orders: b.email_for_orders, estimated_delivery_time_minutes: b.estimated_delivery_time_minutes, lat: b.lat || '', lng: b.lng || '', active: b.active }); }} className="p-2 text-zinc-400 hover:text-[#E84C1C] rounded-lg hover:bg-zinc-800 transition-colors"><Pencil size={15} /></button>
                    <button onClick={() => remove(b.id)} className="p-2 text-zinc-400 hover:text-red-400 rounded-lg hover:bg-zinc-800 transition-colors"><Trash2 size={15} /></button>
                  </div>
                </div>

                {/* Operations panel */}
                {opsPanel === b.id && (
                  <div data-testid={`ops-panel-${b.id}`} className="border-t border-zinc-800/50 p-4 bg-zinc-900/80 space-y-3">
                    <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Control operativo</h4>

                    {/* Close today */}
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-zinc-300">Cerrar hoy</p>
                        <p className="text-[10px] text-zinc-600">Bloquea pedidos por el día completo</p>
                      </div>
                      <Switch data-testid={`close-toggle-${b.id}`} checked={!!b.is_closed_today} onCheckedChange={v => { if (v && !closeReason) { /* toggle on */ } toggleClose(b.id, v); }} />
                    </div>
                    {!b.is_closed_today && (
                      <Input data-testid={`close-reason-${b.id}`} value={closeReason} onChange={e => setCloseReason(e.target.value)} placeholder="Motivo (opcional): Feriado, mantenimiento..." className="bg-zinc-950 border-zinc-800 h-8 text-xs" />
                    )}
                    {b.is_closed_today && b.closed_reason && <p className="text-xs text-red-400/80">Motivo: {b.closed_reason}</p>}

                    {/* Pause */}
                    <div>
                      <p className="text-sm font-medium text-zinc-300 mb-2">Pausar pedidos</p>
                      {isPaused ? (
                        <div className="flex items-center gap-2">
                          <p className="text-xs text-amber-400">Pausada hasta {new Date(b.pause_until).toLocaleTimeString('es-PY', { hour: '2-digit', minute: '2-digit' })}</p>
                          <button onClick={() => pause(b.id, 0)} data-testid={`unpause-${b.id}`} className="flex items-center gap-1 text-xs text-emerald-400 hover:text-emerald-300 px-2 py-1 rounded bg-emerald-400/10">
                            <Play size={12} /> Reanudar
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-2">
                          <button onClick={() => pause(b.id, 30)} data-testid={`pause-30-${b.id}`} className="text-xs px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800">30 min</button>
                          <button onClick={() => pause(b.id, 60)} data-testid={`pause-60-${b.id}`} className="text-xs px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800">60 min</button>
                          <button onClick={() => pause(b.id, 90)} className="text-xs px-3 py-1.5 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800">90 min</button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        }
      </div>

      {/* Multi-range schedule editor */}
      {hoursEdit && (
        <div className="mt-4 bg-zinc-900 border border-zinc-800/50 rounded-xl p-4" data-testid="hours-editor">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2"><Clock size={16} className="text-[#E84C1C]" /> Horarios - Sucursal #{hoursEdit}</h3>
            <button onClick={() => setHoursEdit(null)} className="text-zinc-400 hover:text-white"><X size={16} /></button>
          </div>

          {/* Current hours by day */}
          {DAYS.map((day, i) => {
            const dayHours = hours.filter(h => h.day_of_week === i);
            return (
              <div key={i} className="py-2 border-b border-zinc-800/30 last:border-0">
                <p className="text-xs text-zinc-400 font-medium mb-1">{day}</p>
                {dayHours.length === 0 && <p className="text-[10px] text-zinc-600 pl-2">Sin horario</p>}
                <div className="space-y-1">
                  {dayHours.map(h => (
                    <div key={h.id} className="flex items-center gap-2 pl-2">
                      <span className="text-xs text-zinc-300">{h.start_time.slice(0,5)} – {h.end_time.slice(0,5)}</span>
                      <button onClick={() => removeHour(h.id)} className="text-red-400/60 hover:text-red-400"><X size={12} /></button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Add new range */}
          <div className="mt-3 pt-3 border-t border-zinc-800/50 flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-[10px] text-zinc-600">Día</Label>
              <select value={newHour.day_of_week} onChange={e => setNewHour(h => ({ ...h, day_of_week: parseInt(e.target.value) }))} className="bg-zinc-950 border border-zinc-800 rounded-lg px-2 py-1.5 text-xs text-zinc-300 w-28">
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-[10px] text-zinc-600">Desde</Label>
              <Input type="time" value={newHour.start_time} onChange={e => setNewHour(h => ({ ...h, start_time: e.target.value }))} className="bg-zinc-950 border-zinc-800 h-8 w-28 text-xs" />
            </div>
            <div>
              <Label className="text-[10px] text-zinc-600">Hasta</Label>
              <Input type="time" value={newHour.end_time} onChange={e => setNewHour(h => ({ ...h, end_time: e.target.value }))} className="bg-zinc-950 border-zinc-800 h-8 w-28 text-xs" />
            </div>
            <button onClick={addHour} data-testid="add-hour-btn" className="flex items-center gap-1 text-xs bg-[#E84C1C] text-white px-3 py-1.5 rounded-lg hover:bg-[#d43f12]"><Plus size={12} /> Agregar</button>
          </div>
        </div>
      )}
    </div>
  );
}
